/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderNavigationService, BuilderUserContext, FilterNames, FilterRestService } from '@gh/builder-shared';
import {
  AelEvents,
  BuilderPermission,
  BuilderPermissionSet,
  getAelEventsArray,
  ScheduleType,
  SystemRoleBit,
  UserListItemDto,
  ScheduleDetailsDto,
} from '@gh/builder-shared-data';
import {
  cellValueFormatter,
  rendererIconByName,
  textMessageDialogRoute,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import {
  AppRouter,
  DateTimeTypes,
  EnumDictionary,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getAllNewSelectedEntities,
  getRouteInitData,
  LocalizationBusinessService,
  pollReportingStatus,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { MessageBasket } from '@gh/core-messages';
import {
  AutocompleteLoadMoreEvent,
  Confirmation,
  DialogService,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { of$, throwError$ } from '@gh/rx';
import { catchError$, filter$, map$, mapTo$, switchMap$ } from '@gh/rx/operators';

import { omit, pick } from 'lodash';

import {
  ScheduleJobProgressFilter,
  ScheduleJobProgressFilterDialogConfig,
} from '../../resources/schedule-details.model';
import { DATE_FILTER, ScheduleFinderDialogConfig } from '../../resources/schedule-finder-business.service';
import { ScheduleFinderListItem } from '../../resources/schedule-finder.model';
import {
  JobScheduleCreateDialogConfig,
  ScheduleListBusinessService,
} from '../../resources/schedule-list-business.service';
import {
  ConstructionButStatus,
  ConstructionScheduleFilter,
  ConstructionScheduleListItem,
  prepareJobProgressFilter,
} from '../../resources/schedule-list.model';
import { jobScheduleCreateDialogRoute } from '../job-schedule-create-dialog/job-schedule-create-dialog.component';
import { scheduleFinderDialogRoute } from '../schedule-finder-dialog/schedule-finder-dialog.route';
import {
  scheduleJobProgressFilterDialogRoute,
} from '../schedule-job-progress-filter-dialog/schedule-job-progress-filter-dialog.component';
import {
  scheduleSupervisorsReportFilterDialogRoute,
} from '../schedule-supervisors-report-filter-dialog/schedule-supervisors-report-filter-dialog.component';
import {
  scheduleVendorReportFilterDialogRoute,
} from '../schedule-vendor-filter-dialog/schedule-vendor-filter-dialog.component';
import { noop, Observable, Subscription } from 'rxjs';
import { createLink } from '@gh/core-util';
import { TranslationService } from '@gh/core-mls';
import {
  scheduleConstructionReportDialogRoute,
} from '../schedule-construction-report-dialog/schedule-construction-report-dialog.component';
import { ID } from 'common-shared-data/common';

@Component({
  selector: 'gh-construction-schedule-list',
  template: require('./construction-schedule-list.component.html'),
  styles: [require('./construction-schedule-list.component.scss')],
})
export class ConstructionScheduleListComponent implements OnInit, OnDestroy {
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();
  dataSource: SlicedDataSource<ConstructionScheduleListItem>;
  form: FilterableFormState<ConstructionScheduleFilter>;
  isEstimatorSeeSupervisorReports: boolean;
  isSupervisorReportsIncludeUnsentOrders: boolean;
  isCloud: boolean;
  filter: any;
  isInProgress: boolean;
  supervisors: UserListItemDto[];
  butStatus: ConstructionButStatus[];
  activeTask: Maybe<ConstructionButStatus>;
  dateFilter = DATE_FILTER;
  supervisorsConst = [
    {userName: this.translations.get('lbl_all'), userId: 'ALL'},
    {userName: this.translations.get('lbl_unassigned'), userId: 'UNASSIGNED'},
  ];
  private produceSubscription: Subscription;

  private scheduleTypeDict: EnumDictionary<ScheduleType>;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private userContext: BuilderUserContext,
              private routeData: RouteDataService,
              private formStateBuilder: FormStateBuilder,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private filterService: FilterRestService,
              private waitingService: WaitingService,
              private navigations: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private localizationBusinessService: LocalizationBusinessService,
              private translations: TranslationService,
              private business: ScheduleListBusinessService) {
  }

  get isSupervisorsReportAllowed(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.Reports.Schedules.SUP_SCHEDULE)
      || (this.userContext.hasSystemRole(SystemRoleBit.Estimator)
        && this.isEstimatorSeeSupervisorReports);
  }

  get isVendorReportAllowed(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.Reports.Schedules.VENDOR_REPORT);
  }

  get isJobProgressReportAllowed(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.Reports.Schedules.JOB_PROGRESS);
  }

  get isScheduleReportAllowed(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
    || this.userContext.hasSystemRole(SystemRoleBit.Master)
    || this.userContext.hasPermission(BuilderPermission.Reports.Schedules.CONSTRUCTION_SCHEDULE);
  }

  get isToolbarFileButtonVisible(): boolean {
    return this.isSupervisorsReportAllowed
      || this.isJobProgressReportAllowed
      || this.isVendorReportAllowed
      || this.isScheduleReportAllowed;
  }

  get producingLabel(): string {
    return this.translations.get('Report are currently in progress. Please wait.');
  }

  get isShowMyJobsDisabled(): boolean {
    if (!this.form) {
      return true;
    }
    return this.form.getControl('supervisorId').value === this.userContext.data.id;
  }

  ngOnInit(): void {
    const {filter: initFilter, supervisors, init, gridLayout, scheduleTypeDict, printers } = getRouteInitData(this.route);
    let isInit = true;
    this.supervisors = [
      ...this.supervisorsConst,
      ...supervisors];
    this.isEstimatorSeeSupervisorReports = init.isEstimatorSeeSupervisorReports;
    this.isSupervisorReportsIncludeUnsentOrders = init.isSupervisorReportsIncludeUnsentOrders;
    this.isCloud = !!printers.isCloud;
    this.butStatus = init.bulkUpdateTasksStatus;
    this.activeTask = this.butStatus.find((el) => el.isInProgress);
    this.isInProgress = this.activeTask ? this.activeTask.isInProgress : false;
    this.scheduleTypeDict = scheduleTypeDict;
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        jobNumber: {
          // list always gets sorted from BE
          comparator: () => 0,
        },
        startDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        endDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        isTodoExist: { cellRenderer: truthyCellRenderer(rendererIconByName('done')) },
        duration: {
          valueFormatter: cellValueFormatter<number>((duration) =>
            this.localizationBusinessService.formatHoursDuration(duration)),
        },
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowNodeId: (schedule: ConstructionScheduleListItem) => schedule.scheduleId,
      });
    this.gridDefinition.bind(gridLayout);

    let supervisor = this.supervisorsConst.find((item) => item.userId === 'ALL');
    if (initFilter.supervisorId) {
      supervisor = this.supervisors.find((user) => user.userId === initFilter.supervisorId);
    } else if (initFilter.showSupervisors === 'UNASSIGNED') {
      supervisor = this.supervisorsConst.find((item) => item.userId === initFilter.showSupervisors);
    }
    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: {
        ...initFilter,
        supervisor,
        handoverDate: initFilter.handoverDate || 'ALL',
      },
      children: {
        showSupervisors: void 0,
        supervisorId: void 0,
        supervisor: void 0,
        showActive: void 0,
        searchString: void 0,
        showFinalized: void 0,
        handoverDate: void 0,
        showMyJobs: void 0,
        showCompleted: void 0,
      },
    }));

    const tracker = this.routeData
      .tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.filter = this.getFilterValue(filter);
      this.dataSource = this.business.findAllConstructionSchedulesAsDataSource(this.filter);
      if (!isInit) {
        this.filterService.update(FilterNames.constructionSchedule, this.filter).subscribe();
      }
      isInit = false;
    });

    if (this.activeTask && !this.isCloud) {
      const taskType = this.activeTask.bulkUpdateTaskType;

      this.produceSubscription = pollReportingStatus({
        status: of$({ processed: !this.isInProgress }),
        poll: () => this.business.getProduceStatus(taskType),
      }).subscribe((event) => {
        this.isInProgress = !event.nextStatus.processed;

        if (!this.isInProgress) {
          this.business.gets3Link(taskType).pipe(
            this.waitingService.operator(),
            switchMap$((report) => report.s3Link ? this.showReportDialog(report) : of$('')))
            .subscribe(noop, this.notifications.processAll);
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.produceSubscription) {
      this.produceSubscription.unsubscribe();
    }
  }

  getFilterValue(filter: any): any {
    const excludedPaths = ['supervisor'];
    if (!filter.supervisorId || filter.supervisorId === 'UNASSIGNED' || filter.supervisorId === 'ALL') {
      excludedPaths.push('supervisorId');
    }
    if (!filter.showSupervisors) {
      excludedPaths.push('showSupervisors');
    }
    return omit(filter, excludedPaths);
  }

  onCreate(): void {
    this.dialogs.openWithInput(jobScheduleCreateDialogRoute, <JobScheduleCreateDialogConfig>{
      type: ScheduleType.Construction,
      helpId: 'schedules/constructions/details',
    })
    .afterClosed()
    .pipe(filter$(Boolean))
    .subscribe((init: { jobId: ID; templateId: ID }) => {
      this.business.init(init.jobId, init.templateId, ScheduleType.Construction)
        .subscribe(
          (scheduleDetails: ScheduleDetailsDto) => {
            if (scheduleDetails.errorMessage && scheduleDetails.errorMessage.trim() !== "") {
              this.notifications.warning("msg_cyclic_depedency", { issues: scheduleDetails.errorMessage });
            } else {
              this.router.navigateToChild(['schedules/construction/new/gantt-chart'], {
                queryParams: pick(init, 'jobId', 'templateId'),
              });
            }
          }
        );
    });
  }
  

  onSearch(): void {
    this.form.emitFilter();
  }

  goToAel(schedule?: ConstructionScheduleListItem): void {
    this.navigations.goToAel({
      label: 'lbl_construction_schedules',
      entityType: 'JOB',
      entityId: schedule ? schedule.jobId : void 0,
      eventTypeIds: getAelEventsArray(AelEvents.Job.ConstructionSchedule),
    });
  }

  onOpenCombinedGanttChart(): void {
    this.dialogs.openWithInput(scheduleFinderDialogRoute, <ScheduleFinderDialogConfig>{
      type: ScheduleType.Construction,
      multiselection: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((items: ScheduleFinderListItem[]) => {
        this.router.navigateToChild(['schedules/construction/combined/gantt-chart'], {
          queryParams: { scheduleIds: items.map((item) => item.scheduleId).join() },
        });
      });
  }

  onOpen(schedule: ConstructionScheduleListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Schedules.Construction.EDIT_VIEW)) {
      this.router.navigateToChild(['schedules/construction', schedule.scheduleId, 'gantt-chart']);
    }
  }

  onRemove(schedule: ConstructionScheduleListItem): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_sch_delete', {
      schType: this.scheduleTypeDict.get(ScheduleType.Construction),
      jobNumber: schedule.jobNumber,
    })).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.removeConstructionSchedule(schedule).pipe(this.waitingService.operator())))
      .subscribe(
        () => this.loadData(),
        this.notifications.processAll);
  }

  onOpenJobProgressReport(): void {
    this.dialogs.openWithInput(scheduleJobProgressFilterDialogRoute, <ScheduleJobProgressFilterDialogConfig>{
      type: 1,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((jobProgressFilter: ScheduleJobProgressFilter) => {
        this.business
          .deliverJobProgressReport(prepareJobProgressFilter(jobProgressFilter, {
            skipSupplierDeliveryMethodCheck: false,
          })).pipe(
          this.waitingService.operator(),
          catchError$((error: MessageBasket) => {
            if (this.isCloud) {
              return error.has('msg_sch_no_printer_email')
                ? this.dialogs.confirmation(yesNoConfirmation('msg_sch_no_printer_email'))
                : throwError$(error);
            } else {
              return throwError$(error);
            }
          }),
          switchMap$((answer: Confirmation) => yesAnswer(answer)
            ? this.business.deliverJobProgressReport(prepareJobProgressFilter(jobProgressFilter, {
              skipSupplierDeliveryMethodCheck: true,
            })).pipe(this.waitingService.operator(), mapTo$(answer))
            : of$(answer)))
          .subscribe((answer: Confirmation) => {
            if (this.isCloud) {
              if (yesAnswer(answer)) {
                this.notifications.success('msg_job_progress_tasks_added');
              }
            } else {
              this.isInProgress = true;
              this.produceSubscription = pollReportingStatus({
                status: of$({ processed: !this.isInProgress }),
                                                               // tslint:disable-next-line:no-magic-numbers
                poll: () => this.business.getProduceStatus(22),
              }).subscribe((event) => {
                this.isInProgress = !event.nextStatus.processed;

                if (!this.isInProgress) {
                  // tslint:disable-next-line:no-magic-numbers
                  this.business.gets3Link(22).pipe(
                    this.waitingService.operator(),
                    switchMap$((report) => report.s3Link ? this.showReportDialog(report) : of$('')))
                    .subscribe(noop, this.notifications.processAll);
                }
              });
            }
          }, this.notifications.processAll); // tslint:disable-line:align
      });
  }

  onOpenSupervisorsReport(): void {
    this.dialogs.openWithInput(scheduleSupervisorsReportFilterDialogRoute, {
      isIncludeUnsentOrders: this.isSupervisorReportsIncludeUnsentOrders,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe(() => {
        this.isInProgress = true;
        this.produceSubscription = pollReportingStatus({
          status: of$({ processed: !this.isInProgress }),
                                                         // tslint:disable-next-line:no-magic-numbers
          poll: () => this.business.getProduceStatus(25),
        }).subscribe((event) => {
          this.isInProgress = !event.nextStatus.processed;

          if (!this.isInProgress) {
            // tslint:disable-next-line:no-magic-numbers
            this.business.gets3Link(25).pipe(
              this.waitingService.operator(),
              switchMap$((report) => report.s3Link ? this.showReportDialog(report) : of$('')))
              .subscribe(noop, this.notifications.processAll);
          }
        });
      });
  }

  onOpenVendorReport(): void {
    this.dialogs.openWithInput(scheduleVendorReportFilterDialogRoute, {})
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe(() => {
        this.isInProgress = true;
        this.produceSubscription = pollReportingStatus({
          status: of$({ processed: !this.isInProgress }),
                                                         // tslint:disable-next-line:no-magic-numbers
          poll: () => this.business.getProduceStatus(26),
        }).subscribe((event) => {
          this.isInProgress = !event.nextStatus.processed;

          if (!this.isInProgress) {
            // tslint:disable-next-line:no-magic-numbers
            this.business.gets3Link(26).pipe(
              this.waitingService.operator(),
              switchMap$((report) => report.s3Link ? this.showReportDialog(report) : of$('')))
              .subscribe(noop, this.notifications.processAll);
          }
        });
      });
  }

  onOpenWorksheet(): void {
    this.router.navigateToChild(['schedules/construction/worksheet']);
  }

  onOpenScheduleReport(): void {
    this.dialogs.openWithInput(scheduleConstructionReportDialogRoute, {})
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe(() => {
        this.isInProgress = true;
        this.produceSubscription = pollReportingStatus({
                                                         status: of$({ processed: !this.isInProgress }),
                                                         // tslint:disable-next-line:no-magic-numbers
                                                         poll: () => this.business.getProduceStatus(26),
                                                       }).subscribe((event) => {
          this.isInProgress = !event.nextStatus.processed;

          if (!this.isInProgress) {
            // tslint:disable-next-line:no-magic-numbers
            this.business.gets3Link(26).pipe(
              this.waitingService.operator(),
              switchMap$((report) => report.s3Link ? this.showReportDialog(report) : of$('')))
              .subscribe(noop, this.notifications.processAll);
          }
        });
      });
  }

  loadSupervisors(event: AutocompleteLoadMoreEvent): void {
    event.load(of$(this.supervisors.filter((user) => user.userName.toLowerCase().includes(event.query.filter?.toLowerCase()))));
  }

  onSupervisorSelected(supervisor: UserListItemDto): void {
    if (!supervisor) {
      this.form.getControl('supervisorId').setValue('');
      this.form.getControl('showSupervisors').setValue('');
    } else if (supervisor.userId === 'UNASSIGNED') {
      this.form.getControl('supervisorId').setValue('UNASSIGNED');
      this.form.getControl('showSupervisors').setValue('UNASSIGNED');
    } else if (supervisor.userId === 'ALL') {
      this.form.getControl('supervisorId').setValue('ALL');
      this.form.getControl('showSupervisors').setValue('ALL');
    } else if (!supervisor || supervisor.userId !== this.form.getControl('supervisorId').value) {
      if (this.userContext.data.id === supervisor.userId) {
        this.form.getControl('showMyJobs').setValue('false');
      }
      this.form.getControl('supervisorId').setValue(supervisor.userId);
      this.form.getControl('showSupervisors').setValue('ID');
    }
    this.onSearch();
  }

  private loadData(): void {
    this.dataSource = this.business.findAllConstructionSchedulesAsDataSource(this.filter);
  }

  private showReportDialog(report: any): Observable<void> {
    return this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
      title: this.translations.get('Link to Print Report'),
      html: [
        this.translations.get('Please follow the link below to print Report'),
        createLink(report.s3Link, 'Your file'),
      ].join(' '),
      buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
    }).afterClosed();
  }
}
